"use client"

import type React from "react"
import { useState, useRef, useEffect } from "react"
import { Mail, ArrowRight, X } from "lucide-react"
import { postRegisterWithOffer, postSendCodeEmail } from "../../../services/auth/authAxios/authAxios"
import { toast } from "react-hot-toast"

interface EmailStepProps {
  email: string
  onNext: (email: string) => void
  onClose: () => void
  offer?: boolean
  handlePagoIniciado?: () => void
}

const EmailStep: React.FC<EmailStepProps> = ({ email: initialEmail, onNext, onClose, offer, handlePagoIniciado }) => {
  const [email, setEmail] = useState(initialEmail)
  const [error, setError] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const modalRef = useRef<HTMLDivElement>(null)

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.trim()) {
      setError("El correo electrónico es requerido")
      return
    }
    if (!validateEmail(email)) {
      setError("Por favor ingresa un correo electrónico válido")
      return
    }
    setError("")
    setIsLoading(true)

    if (offer) {
      postRegisterWithOffer({ email })
        .then((res) => {
          handlePagoIniciado?.()
          localStorage.setItem('access_token', res.access_token)
          localStorage.setItem('refresh_token', res.access_token)
          window.location.href = res.payment_url
        })
        .catch((err) => {
          window.location.href = '/login?email=' + email
        })
        .finally(() => {
          setIsLoading(false)
        })
    } else {
      postSendCodeEmail({ email })
        .then((res) => {
          onNext(res.data.email)
          toast.success(res.data.message)
        })
        .catch((err) => {
          toast.error(err.message)
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      onClose()
    }
  }

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose()
      }
    }

    document.addEventListener("keydown", handleEscape)
    return () => document.removeEventListener("keydown", handleEscape)
  }, [onClose])

  if (offer) {
    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
        onClick={handleOverlayClick}
      >
        <div
          ref={modalRef}
          className="bg-white rounded-2xl shadow-xl p-8 transition-all duration-300 hover:shadow-2xl relative max-w-md w-full"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all duration-200"
            aria-label="Cerrar modal"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
              <Mail className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{offer ? 'Comienza vender con IA' : 'Verificación de Email'}</h1>
            <p className="text-gray-600">{offer ? '¿Con que correo quieres empezar tu experiencia en Fluxi?' : 'Ingresa tu correo electrónico para recibir un código de verificación'}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" translate="no">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Correo electrónico
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  if (error) setError("")
                }}
                className={`w-full px-4 py-3 border rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${error ? "border-red-300 bg-red-50" : "border-gray-300 hover:border-gray-400"
                  }`}
                placeholder="tu@ejemplo.com"
                autoFocus
                disabled={isLoading}
              />
              {error && <p className="mt-2 text-xs text-red-600 animate-fade-in">{error}</p>}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 flex items-center justify-center group disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Enviando código...
                </>
              ) : (
                <>
                  Enviar código
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-200" />
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    )
  } else {
    return (
      <div className="w-full">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            {/* <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Correo electrónico
            </label> */}
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                if (error) setError("")
              }}
              className={`w-full px-4 py-3 border rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${error ? "border-red-300 bg-red-50" : "border-gray-300 hover:border-gray-400"
                }`}
              placeholder="tu@ejemplo.com"
              autoFocus
              disabled={isLoading}
            />
            {error && <p className="mt-2 text-xs text-red-600 animate-fade-in">{error}</p>}
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-[#85A2FF] text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 flex items-center justify-center group disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Enviando código...
              </>
            ) : (
              <>
                Enviar código
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-200" />
              </>
            )}
          </button>
        </form>
      </div>
    )
  }
}

export default EmailStep
